/**
 * Module data layer + access boundary — now backed by the API.
 *
 * THE READS STAYED SYNCHRONOUS ON PURPOSE
 * `listAllModules()` is called from a `useState` initialiser and from render
 * paths. Turning it into a promise would have pushed a loading state into
 * every screen that touches a module, which is a far larger change than
 * moving the storage. Instead the modules are fetched ONCE after sign-in
 * (`hydrate`) and held in memory; the readers below are unchanged in shape and
 * read that cache. The writers are the ones that became async, because a save
 * that may fail has to be awaited by someone who can say so.
 *
 * THE BOUNDARY MOVED, AND IS NOW REAL
 * The old comment here said the filtering "is not a substitute for" server-side
 * scoping. It is no longer doing that job alone: `GET /modules` returns only
 * what the caller may see, so an unassigned module never reaches the browser.
 * `listModulesForUser` is kept because it still answers the same question
 * about an already-loaded set, and because a stale cache should not out-live
 * a revoked grant on screen.
 */

import type { AppModule, User } from '../types';
import { ApiError, api } from './apiClient';
import {
  ModulePermissionError,
  canDeleteModule,
  canEditModule,
} from '../auth/modulePermissions';

/** Owner assigned to modules created before RBAC existed. */
export const ADMIN_OWNER_ID = 'usr_admin';

/** What the API stores for a module: metadata plus the whole document. */
interface ApiModule {
  id: string;
  kind: 'map' | 'dashboard';
  title: string;
  ownerId: string;
  visibility: 'admin-workspace' | 'user-owned';
  version: number;
  createdAt: string;
  updatedAt: string;
  sharedWithOrg: boolean;
  config: Record<string, unknown>;
  assignedUserIds: string[];
}

/** A module's current direct-share grant: which users, and which groups. */
export interface ModuleShareState {
  userIds: string[];
  groupIds: string[];
}

/**
 * The in-memory copy of what the server has.
 *
 * Module-level rather than React state because the readers are plain
 * functions called from many places, exactly as they were before.
 */
let cache: AppModule[] = [];
let hydrated = false;

/**
 * Server versions, kept beside the cache rather than inside the module.
 *
 * `AppModule` is the document the builder edits and saves back wholesale; a
 * version stored inside it would be overwritten by the editor's own copy and
 * the optimistic-locking check would compare a number against itself.
 */
const versions = new Map<string, number>();

/**
 * The server's fields, folded back into the document.
 *
 * `config` is the module as the app knows it. The columns beside it are the
 * server's authority on ownership and visibility, so they win: a document that
 * somehow carried a different ownerId cannot re-parent itself by being saved.
 */
function fromApi(row: ApiModule): AppModule {
  return {
    ...(row.config as unknown as AppModule),
    id: row.id,
    title: row.title,
    ownerId: row.ownerId,
    visibility: row.visibility,
    createdAt: row.createdAt,
    updatedAt: row.updatedAt,
    sharedWithOrg: row.sharedWithOrg,
  };
}

/** The document to send back up, with the server's own columns lifted out. */
/**
 * A layer with `sharedLayerId` carries no `localData` of its own once the
 * server has resolved it back in for display — see `services.resolve_shared_layers`
 * on the backend. Saving THAT snapshot back would freeze a stale copy into
 * this module and defeat the whole point of sharing it (the server strips it
 * too, defensively — see `strip_shared_layer_snapshots` — but doing it here
 * as well means a save never even sends the payload for data this module no
 * longer owns).
 */
function stripSharedLayerSnapshots(module: AppModule): AppModule {
  const layers = (module as { layers?: unknown }).layers;
  if (!Array.isArray(layers) || !layers.some((layer) => (layer as { sharedLayerId?: string })?.sharedLayerId)) {
    return module;
  }
  return {
    ...module,
    layers: layers.map((layer) => {
      const entry = layer as { sharedLayerId?: string; localData?: unknown };
      if (!entry.sharedLayerId) return layer;
      const { localData, ...rest } = entry as Record<string, unknown>;
      return rest;
    }),
  } as AppModule;
}

function toConfig(module: AppModule): Record<string, unknown> {
  return stripSharedLayerSnapshots(module) as unknown as Record<string, unknown>;
}

export const moduleService = {
  /**
   * Loads every module this user may see. Call once after sign-in.
   *
   * Returns the list so the caller can seed React state from the same value
   * rather than reading the cache a moment later and hoping.
   */
  async hydrate(): Promise<AppModule[]> {
    const rows = await api.get<ApiModule[]>('/modules');
    versions.clear();
    for (const row of rows) versions.set(row.id, row.version);
    cache = rows.map(fromApi);
    hydrated = true;
    return cache;
  },

  /**
   * The COMPLETE document for one module.
   *
   * `hydrate` now loads summaries — enough to draw the portal, without the
   * uploaded features that make a module megabytes. Anything that renders a
   * module (both builders, both viewers) needs the whole thing, so it asks for
   * it here when the module is actually opened.
   *
   * Refreshing the stored version is not a side effect worth hiding: opening a
   * module is exactly when the client should learn what the server currently
   * holds, which is also why a save made straight afterwards cannot be stale.
   */
  async loadFull(id: string): Promise<AppModule> {
    const row = await api.get<ApiModule>(`/modules/${id}`);
    versions.set(row.id, row.version);
    const full = fromApi(row);
    cache = cache.map((module) => (module.id === row.id ? full : module));
    return full;
  },

  /** Drops everything held for the previous session. Call on sign-out. */
  reset(): void {
    cache = [];
    versions.clear();
    hydrated = false;
  },

  /** True once `hydrate` has completed; false before the first load. */
  isReady(): boolean {
    return hydrated;
  },

  /** Everything currently loaded — already scoped to the caller by the API. */
  listAllModules(): AppModule[] {
    return cache;
  },

  /**
   * THE ACCESS BOUNDARY, as applied to an already-loaded set.
   *
   * The set itself IS the boundary: `GET /modules` only ever returns what the
   * SIGNED-IN user may see — owned, admin-assigned or directly shared, OR
   * granted through a group (see modules_router.py's `_visible_modules`,
   * which resolves all four). This used to re-filter that already-correct
   * list a second time using `ownerId`/`user.assignedModuleIds`, but
   * `assignedModuleIds` mirrors only the direct-share table (see
   * `services.py`'s `assigned_module_ids`) — it has no idea a group grant
   * exists at all. A module someone could only see because of a group
   * membership was correctly included by the server and then silently
   * dropped again right here, which is why a group's shared apps were
   * missing from the home page even though "My groups" (which reads the
   * same cache without this extra filter) showed them correctly.
   *
   * `allModules` is only ever the cache itself or a copy of it at every real
   * call site, so there is nothing left for this to filter — trusting what
   * the server already decided is not just simpler, it is the only version
   * of this check that knows about groups.
   */
  listModulesForUser(user: User | null, allModules?: AppModule[]): AppModule[] {
    if (!user) return [];
    return allModules ?? cache;
  },

  /** Single-module guard, used before opening a module by id/deep link.
   *  Same reasoning as `listModulesForUser` above — `module` was only ever
   *  in `cache` because a `GET /modules` (or the single-module fetch for the
   *  module actually being opened) already decided this user may see it. */
  canAccessModule(user: User | null, module: AppModule | null): boolean {
    return Boolean(user) && Boolean(module);
  },

  getModuleById(id: string): AppModule | null {
    return cache.find((module) => module.id === id) ?? null;
  },

  /**
   * Pushes a whole list up, one request per changed module.
   *
   * Kept because the app has one path that replaces the set wholesale. It is
   * NOT a bulk endpoint: sending fifty unchanged modules on every save would
   * be fifty pointless writes, so only what actually differs is sent.
   */
  async saveAll(modules: AppModule[]): Promise<AppModule[]> {
    for (const module of modules) {
      const existing = cache.find((entry) => entry.id === module.id);
      if (!existing) {
        await moduleService.createModule(module);
      } else if (JSON.stringify(existing) !== JSON.stringify(module)) {
        await moduleService.updateModule(null, module, { skipPermissionCheck: true });
      }
    }
    return cache;
  },

  /** Creates a module owned by the signed-in user. */
  async createModule(module: AppModule): Promise<AppModule[]> {
    const row = await api.post<ApiModule>('/modules', {
      id: module.id,
      kind: (module as { kind?: string }).kind === 'dashboard' ? 'dashboard' : 'map',
      title: module.title || (module as { name?: string }).name || 'Untitled',
      visibility: module.visibility ?? 'admin-workspace',
      config: toConfig(module),
    });
    versions.set(row.id, row.version);
    cache = [...cache, fromApi(row)];
    return cache;
  },

  /* ───────────────────────────────────────────────────────────────────────
   * GUARDED MUTATORS
   *
   * The client-side check is kept as a fast, clear failure — it produces the
   * message the UI shows. It is no longer the ONLY check: the API refuses the
   * same operations, so bypassing the UI achieves nothing.
   * ─────────────────────────────────────────────────────────────────────── */

  /**
   * Edit / rename / save. Throws ModulePermissionError if not permitted.
   *
   * RECOVERING FROM A STALE VERSION
   * The server refuses a write whose `expectedVersion` does not match, which is
   * right — two people editing one module must not silently overwrite each
   * other. But this number lives in a Map in ONE page's memory, and a page can
   * come back holding an old one without anybody having edited anything: press
   * Save & launch, then the browser's Back button, and the restored page still
   * believes the version it had before the save. Every save from then on is
   * refused, forever, for a document nobody else has touched.
   *
   * Observed in production: one PUT answering 200 and then fourteen 409s in a
   * row on the same module, with the database sitting at exactly one increment.
   *
   * So a conflict is no longer fatal on the first try. The current version is
   * re-read and the write is attempted once more. If THAT is refused as well,
   * something is genuinely racing this save and the error stands.
   *
   * The retry can overwrite a newer copy, so it never happens quietly:
   * `onOverwroteNewer` fires and the caller says so. Losing a save silently is
   * the outcome this whole mechanism exists to prevent — but so is a builder
   * that cannot save at all, and only one of those two was actually happening.
   */
  async updateModule(
    user: User | null,
    updated: AppModule,
    options: { skipPermissionCheck?: boolean; onOverwroteNewer?: () => void } = {},
  ): Promise<AppModule[]> {
    const existing = cache.find((module) => module.id === updated.id) ?? null;

    if (!existing) {
      // Creating a brand-new module is allowed for any signed-in user; they
      // become its owner.
      return moduleService.createModule(updated);
    }

    if (!options.skipPermissionCheck && !canEditModule(user, existing)) {
      throw new ModulePermissionError('edit', existing.title || existing.name);
    }

    /* A summary must never be written back. It is the whole document with the
       uploaded features removed, so saving one would delete every file-backed
       layer's data from the server — silently, and irreversibly. Nothing does
       this today (the builders are handed `loadFull` documents), which is
       precisely why it is worth a guard: the next caller will not know. */
    if ((updated as { __partial?: boolean }).__partial) {
      throw new Error(
        'Refusing to save a partially-loaded module. Open it first so the ' +
          'complete document is loaded.',
      );
    }

    const body = {
      title: updated.title || (updated as { name?: string }).name || existing.title,
      visibility: existing.visibility,
      config: toConfig({
        ...updated,
        // Ownership is immutable through an edit — a shared module can never
        // be silently re-parented to the editor. The server enforces this too.
        ownerId: existing.ownerId,
        visibility: existing.visibility,
        createdAt: existing.createdAt,
      }),
    };

    let row: ApiModule;
    try {
      row = await api.put<ApiModule>(`/modules/${updated.id}`, {
        ...body,
        expectedVersion: versions.get(updated.id),
      });
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 409) throw error;

      /* Re-read what the server actually holds, then try once more against it.
         `?summary=1` because only the version is wanted: the full document
         carries every uploaded feature, and the access log showed this pulling
         12 MB to look at one integer. */
      const current = await api.get<ApiModule>(`/modules/${updated.id}?summary=1`);
      versions.set(current.id, current.version);
      row = await api.put<ApiModule>(`/modules/${updated.id}`, {
        ...body,
        expectedVersion: current.version,
      });
      options.onOverwroteNewer?.();
    }

    versions.set(row.id, row.version);
    cache = cache.map((module) => (module.id === row.id ? fromApi(row) : module));
    return cache;
  },

  /** Delete. Throws ModulePermissionError if not permitted, or a plain Error if locked. */
  async deleteModule(user: User | null, moduleId: string): Promise<AppModule[]> {
    const existing = cache.find((module) => module.id === moduleId) ?? null;
    if (!existing) return cache;

    if (!canDeleteModule(user, existing)) {
      throw new ModulePermissionError('delete', existing.title || existing.name);
    }
    // A lock is not a permission — an admin who owns every module could
    // still delete this one, which is exactly what it exists to stop until
    // someone deliberately turns it off. Checked again on the server, in
    // `delete_module`, for the same reason every other guard here is.
    if (existing.locked) {
      throw new Error(`"${existing.title || existing.name}" is locked. Unlock it before deleting.`);
    }

    await api.delete<void>(`/modules/${moduleId}`);
    versions.delete(moduleId);
    cache = cache.filter((module) => module.id !== moduleId);
    return cache;
  },

  /**
   * Patches the App Gallery's own few fields — home-page visibility, the
   * thumbnail, the lock, home-page position — WITHOUT loading or resending
   * the full document.
   *
   * `cache` only ever holds a SUMMARY of a module (see `fromApi`/the
   * server's `summary_config`), and `updateModule` above refuses to save a
   * summary back precisely because doing so would erase every uploaded
   * layer's features. Toggling "show on home page" from the gallery list
   * must not force a full open-then-save just to flip one flag, so this
   * goes through its own narrow endpoint instead — the same reasoning as
   * `/access` below, which replaces a different LEDGE of the document by
   * merging into the config it makes.
   */
  async setGalleryFields(
    user: User | null,
    moduleId: string,
    // `thumbnailUrl: null` clears it. `undefined` would not: `JSON.stringify`
    // drops an undefined property entirely, so the server would receive no
    // field to act on and leave the old thumbnail exactly where it was.
    changes: { showOnHome?: boolean; thumbnailUrl?: string | null; locked?: boolean; homeOrder?: number },
  ): Promise<AppModule[]> {
    const existing = cache.find((module) => module.id === moduleId) ?? null;
    if (existing && !canEditModule(user, existing)) {
      throw new ModulePermissionError('edit', existing.title || existing.name);
    }

    const row = await api.patch<ApiModule>(`/modules/${moduleId}/gallery`, changes);
    versions.set(row.id, row.version);
    cache = cache.map((module) => (module.id === row.id ? fromApi(row) : module));
    return cache;
  },

  /**
   * Silently refreshes this module's own ArcGIS connection token when it is
   * close to expiring — see `AppModule.arcgisTokenExpiresAt` and the
   * App.tsx/EndUserViewer renewal check that calls this.
   *
   * No permission check here, unlike every other write in this file: the
   * server itself only requires VIEW access (see `renew_arcgis_connection`'s
   * own docstring) — an end user just viewing a published app needs their
   * map to keep working too, not just an editor.
   */
  async renewArcgisConnection(
    moduleId: string,
  ): Promise<{ authenticatedBaseUrl: string; expiresAt?: string | null }> {
    return api.post(`/modules/${moduleId}/renew-arcgis-connection`);
  },

  /**
   * Persists a freshly renewed `arcgisUrl` (see `renewArcgisConnection`)
   * without touching anything else about the module — layers, symbology,
   * labelling untouched, same narrow-patch shape as `setGalleryFields`.
   * Also unguarded here for the same reason: the server only requires VIEW
   * access, so a plain viewer's renewal actually sticks for the next
   * person too, rather than repeating on every single visit.
   */
  async patchArcgisConnection(
    moduleId: string,
    changes: {
      arcgisUrl: string;
      arcgisConnectionId?: string;
      arcgisConnectionService?: { name: string; type: 'MapServer' | 'FeatureServer' } | null;
      arcgisTokenExpiresAt?: string | null;
    },
  ): Promise<AppModule[]> {
    const row = await api.patch<ApiModule>(`/modules/${moduleId}/arcgis-connection`, changes);
    versions.set(row.id, row.version);
    cache = cache.map((module) => (module.id === row.id ? fromApi(row) : module));
    return cache;
  },

  /**
   * Turns this module's own "Share with Organization" flag on or off — the
   * OWNER's (or an admin's) call, server-checked the same owner-or-admin
   * way `set_layer_share` already is, deliberately separate from the
   * per-user/per-group `/access` pair below.
   */
  async setOrganizationShare(moduleId: string, sharedWithOrg: boolean): Promise<AppModule[]> {
    const row = await api.patch<ApiModule>(`/modules/${moduleId}/organization-share`, { sharedWithOrg });
    versions.set(row.id, row.version);
    cache = cache.map((module) => (module.id === row.id ? fromApi(row) : module));
    return cache;
  },

  /** Who this module is currently shared with, and which groups — pre-fills
   *  the Share dialog. Gated server-side the same way the write below is. */
  async fetchModuleShare(moduleId: string): Promise<ModuleShareState> {
    return api.get<ModuleShareState>(`/modules/${moduleId}/access`);
  },

  /** Replaces this module's share list AND its group grants in one call —
   *  the owner's (given `modules:share`) or an admin's own save. */
  async setModuleShare(moduleId: string, userIds: string[], groupIds: string[]): Promise<ModuleShareState> {
    return api.put<ModuleShareState>(`/modules/${moduleId}/access`, { userIds, groupIds });
  },
};
